import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Truck, Shield, Clock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ProductCard } from '@/components/product/ProductCard';
import { Button } from '@/components/ui/button';

const features = [
    {
        icon: Truck,
        title: 'Free Shipping',
        description: 'Free delivery on orders over $50',
    },
    {
        icon: Shield,
        title: 'Secure Payment',
        description: 'Your payment information is safe',
    },
    {
        icon: Clock,
        title: '24/7 Support',
        description: 'Round the clock customer service',
    },
];

export function HomePage() {
    const { data: products, isLoading } = useQuery({
        queryKey: ['products', 'featured'],
        queryFn: () => api.get<any[]>('/products?limit=8'),
    });

    return (
        <div className="animate-fade-in">
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-accent/10">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-primary/20 blur-3xl animate-float" />
                    <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-accent/20 blur-3xl animate-float" style={{ animationDelay: '2s' }} />
                </div>

                <div className="container mx-auto relative">
                    <div className="py-24 md:py-32 lg:py-40 text-center max-w-4xl mx-auto">
                        <div className="inline-flex items-center gap-2 rounded-full border bg-background/50 backdrop-blur px-3 py-1 text-sm font-medium mb-6 animate-fade-in-down text-primary">
                            <Sparkles className="h-3 w-3" />
                            New Collection Available
                        </div>

                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6 animate-fade-in-up">
                            Discover Amazing Products{' '}
                            <span className="text-primary">For Your Lifestyle</span>
                        </h1>

                        <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                            Shop the latest trends with fast shipping and easy returns.
                            Quality products at prices you'll love.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                            <Link to="/products">
                                <Button size="lg" className="rounded-full px-8 text-lg h-12">
                                    Shop Now
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                            <Link to="/categories">
                                <Button variant="outline" size="lg" className="rounded-full px-8 text-lg h-12 bg-background/50 backdrop-blur border-primary/20 hover:bg-background/80">
                                    Browse Categories
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Strip */}
            <section className="border-y bg-muted/30 py-8">
                <div className="container mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((feature) => (
                            <div
                                key={feature.title}
                                className="flex items-center gap-4 justify-center"
                            >
                                <div className="p-3 rounded-2xl bg-primary/10 text-primary">
                                    <feature.icon className="h-6 w-6" />
                                </div>
                                <div>
                                    <h3 className="font-semibold">{feature.title}</h3>
                                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products Section */}
            <section className="py-20">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold mb-4">Featured Products</h2>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            Handpicked favorites from our collection that customers love.
                        </p>
                    </div>

                    {isLoading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="aspect-[4/5] rounded-xl bg-muted animate-pulse" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {products?.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                            {products?.length === 0 && (
                                <div className="col-span-4 text-center py-10 text-muted-foreground">
                                    No products found.
                                </div>
                            )}
                        </div>
                    )}

                    <div className="text-center mt-12">
                        <Link to="/products">
                            <Button variant="outline" size="lg">
                                View All Products
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-primary/5">
                <div className="container mx-auto text-center px-4">
                    <h2 className="text-3xl font-bold mb-4">Ready to Start Shopping?</h2>
                    <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                        Join thousands of happy customers and discover why they love shopping with us.
                    </p>
                    <Link to="/products">
                        <Button size="lg">Get Started <ArrowRight className="ml-2 h-5 w-5" /></Button>
                    </Link>
                </div>
            </section>
        </div>
    );
}


// To avoid naming conflict if any, but imports are clean.
// Wait, I imported Button twice? No.
